import { AuthAttribute, userIdToAuthAttribute } from "@megaron/access-control";
import { catalogSignalTypes } from "@megaron/catalog-contracts";
import { crmSignalTypes } from "@megaron/crm-contracts";
import { diffSyncSignalTypes } from "@megaron/diff-sync-contracts";
import { fulfillmentSignalTypes } from "@megaron/fulfillment-contracts";
import { HttpAction } from "@megaron/http-service";
import { HttpUri } from "@megaron/http-uri";
import { invoicesSignalTypes } from "@megaron/invoices-contracts";
import { MegaraxUri } from "@megaron/megarax-uri";
import { baseDocSerializer, ResourceId, signalLevels, Signal, signalSerializer, SignalTypeMeta } from "@megaron/resource";
import { Serializers, SerializerValue, serializerExtensions } from "@megaron/serializers";
import { supportSignalTypes } from "@megaron/support-contracts";

export { signalLevels, signalSerializer };
export type { Signal, SignalTypeMeta };
export type SignalLevel = (typeof signalLevels)[number];

export const signalTypeMeta = {
  ...diffSyncSignalTypes,
  ...catalogSignalTypes,
  ...crmSignalTypes,
  ...invoicesSignalTypes,
  ...supportSignalTypes,
  ...fulfillmentSignalTypes,
} as const;

export type SignalType = keyof typeof signalTypeMeta;

export const signalTypes = Object.keys(signalTypeMeta) as SignalType[];

export const isSignalType = (value: string): value is SignalType => value in signalTypeMeta;

export type SignalSubjectDocType = (typeof signalTypeMeta)[SignalType]["subjectDocType"];

export const signalSubjectDocTypes = [
  ...new Set(signalTypes.map((signalType) => signalTypeMeta[signalType].subjectDocType)),
] as SignalSubjectDocType[];

const legacySignalSubjectDocTypeByValue: Record<string, SignalSubjectDocType> = {
  draft: "invoice-draft",
  ticket: "support-ticket",
};

const normalizeSignalSubjectDocType = (value: string): string =>
  legacySignalSubjectDocTypeByValue[value] ?? value;

const signalSubjectDocTypeCoreSerializer = Serializers.stringOneOf(...signalSubjectDocTypes);

export const signalSubjectDocTypeSerializer = {
  serialize: signalSubjectDocTypeCoreSerializer.serialize,
  deserialize: (raw: unknown) =>
    signalSubjectDocTypeCoreSerializer.deserialize(
      typeof raw === "string" ? normalizeSignalSubjectDocType(raw) : raw,
    ),
  ...serializerExtensions(),
};

export const isSignalSubjectDocType = (value: unknown): value is SignalSubjectDocType =>
  typeof value === "string" && (signalSubjectDocTypes as readonly string[]).includes(value);

export const navBadgeTypes = (...types: SignalType[]): readonly SignalType[] => types;

export const subjectDocTypeForSignalType = (signalType: string): SignalSubjectDocType | null =>
  isSignalType(signalType) ? signalTypeMeta[signalType].subjectDocType : null;

export const signalTypeActionLabel = (signalType: SignalType): string => signalTypeMeta[signalType].actionLabel;

export const defaultSignalInstanceKey = (signalType: string) => signalType;

export const signalInstanceKey = (mentionEventId: string, mentionedUser: string) =>
  `${mentionEventId}:${mentionedUser.toLowerCase()}`;

export const signal = (
  signalType: SignalType,
  params: {
    instanceKey?: string;
    name: string;
    uri?: MegaraxUri | HttpUri;
    recipientAttributes: AuthAttribute[];
    createdAt: Date;
  },
): Signal => {
  const meta = signalTypeMeta[signalType];

  return {
    signalType,
    instanceKey: params.instanceKey ?? defaultSignalInstanceKey(signalType),
    level: meta.level,
    app: meta.app,
    name: params.name,
    uri: params.uri,
    authorizedAttributes: params.recipientAttributes,
    createdAt: params.createdAt,
  };
};

export const mentionSignals = (
  signalType: SignalType,
  params: {
    mentionEventId: string;
    name: string;
    uri: MegaraxUri;
    mentionedUsers: readonly string[];
    createdAt: Date;
  },
): Signal[] =>
  [...new Set(params.mentionedUsers.map((user) => user.toLowerCase()))].map((mentionedUser) =>
    signal(signalType, {
      instanceKey: signalInstanceKey(params.mentionEventId, mentionedUser),
      name: params.name,
      uri: params.uri,
      recipientAttributes: [userIdToAuthAttribute(mentionedUser)],
      createdAt: params.createdAt,
    }),
  );

export const latestMentions = <T>(mentions: readonly T[], createdAt: (mention: T) => Date, limit = 50): T[] =>
  [...mentions].sort((a, b) => createdAt(b).getTime() - createdAt(a).getTime()).slice(0, limit);

export const signalDocIdFromParts = (
  signalType: string,
  subjectDocId: string,
  instanceKey: string,
): ResourceId<"signal"> => {
  if (instanceKey === signalType || instanceKey === defaultSignalInstanceKey(signalType)) {
    return ResourceId("signal", `${signalType}:${subjectDocId}`);
  }

  return ResourceId("signal", `${signalType}:${subjectDocId}:${instanceKey}`);
};

export const signalDocSerializer = Serializers.object({
  ...baseDocSerializer("signal").fields,

  level: Serializers.stringOneOf(...signalLevels),
  app: Serializers.string,
  signalType: Serializers.string,
  instanceKey: Serializers.string.optional(),
  subjectDocType: signalSubjectDocTypeSerializer,
  subjectDocId: Serializers.string,
  createdAt: Serializers.datetime,
  isRead: Serializers.boolean.optional(),
  readAt: Serializers.datetime.nullable().optional(),
});

export type SignalDoc = SerializerValue<typeof signalDocSerializer>;

export const signalRefSerializer = Serializers.object({
  subjectDocType: signalSubjectDocTypeSerializer,
  subjectDocId: Serializers.string,
  signalType: Serializers.string,
  instanceKey: Serializers.string,
});

export type SignalRef = SerializerValue<typeof signalRefSerializer>;

export const signalDocRef = (signal: SignalDoc): SignalRef => ({
  subjectDocType: signal.subjectDocType,
  subjectDocId: signal.subjectDocId,
  signalType: signal.signalType,
  instanceKey: signal.instanceKey ?? defaultSignalInstanceKey(signal.signalType),
});

export const signalActions = {
  searchSignals: HttpAction({
    path: "/signals",
    method: "get",
    requiresAuth: true,
    qsSerializer: Serializers.object({
      app: Serializers.string,
      level: Serializers.stringOneOf(...signalLevels)
        .array()
        .asJsonString(),
      signalType: Serializers.string.array().asJsonString(),
      subjectDocType: signalSubjectDocTypeSerializer,
      subjectDocId: Serializers.string,
      limit: Serializers.integer,
      offset: Serializers.integer,
    }).partial(),
    valueSerializer: Serializers.paginatedList(signalDocSerializer),
  }),

  setSignalRead: HttpAction({
    path: "/signals/read",
    method: "put",
    requiresAuth: true,
    bodySerializer: Serializers.object({
      signals: signalRefSerializer.array(),
      isRead: Serializers.boolean,
    }),
  }),
};
